import React, { useState, DragEvent } from 'react';
import { Abgangsleitung, ConnectedLine } from '../../../../../types/network';
import { LineSegmentState } from '../../../../../store/types/network';
import { findSegmentsConnectedToBus } from '../../../../../utils/networkConnectionUtils';
import { fuseTypes, getRatingsForFuseType } from '../../../../../utils/networkFuseUtils';
import kvsService from '../../../../../services/kvsService';
import { RemoveCableButton } from './RemoveCableButton';
import { ConfirmDialog } from '../../../../shared/ConfirmDialog';
import { useSettingsStore } from '../../../../../store/settingsStore';
import { useNetworkElementsStore } from '../../../../../store/networkElementsStore';
import { fmt } from '../../../../../utils/numberFormat';
import { useReadOnlyUi } from '../../../../../store/featureStore';
import { cableDisplayName, cableTypeLabel } from '../../../../../utils/elementDisplayName';
import { maxPhaseCurrentA } from '../../../../../utils/phaseCurrent';

interface CableRowProps {
  cable: Abgangsleitung;
  connectedLine: ConnectedLine;
  kvsId: string;
  busIndex: number | undefined;
  segments: Record<string, LineSegmentState>;
  thresholds: { green: number; yellow: number; orange: number; red: number };
  onDragStart: (e: DragEvent<HTMLDivElement>, cableId: string) => void;
  onUpdate: () => void;
}

const getLoadStyles = (pct: number, thresholds: { green: number; yellow: number; orange: number; red: number }) => {
  if (pct >= thresholds.red) return { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', bar: 'bg-red-500', mini: 'bg-red-500' };
  if (pct >= thresholds.orange) return { bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', bar: 'bg-orange-500', mini: 'bg-orange-500' };
  if (pct >= thresholds.yellow) return { bg: 'bg-yellow-50 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', bar: 'bg-yellow-500', mini: 'bg-yellow-500' };
  if (pct >= thresholds.green) return { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', bar: 'bg-green-500', mini: 'bg-green-500' };
  return { bg: 'bg-gray-50 dark:bg-primary-700', text: 'text-gray-700 dark:text-primary-300', bar: 'bg-gray-400', mini: 'bg-gray-400' };
};

const getFuseLoadStyles = (pct: number, warning: number, critical: number) => {
  if (pct >= critical) return { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', bar: 'bg-red-500', badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' };
  if (pct >= warning) return { bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', bar: 'bg-orange-500', badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' };
  return { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', bar: 'bg-green-500', badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' };
};

export const CableRow: React.FC<CableRowProps> = ({
  cable,
  connectedLine,
  kvsId,
  busIndex,
  segments,
  thresholds,
  onDragStart,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const readOnly = useReadOnlyUi();
  const [showFuseConfirm, setShowFuseConfirm] = useState(false);
  // A fuse protects the cable it sits on → judged on the cable levels (orange = warning, red = critical).
  const cableLevels = useSettingsStore(state => state.thresholds.cableLoadLevels);

  const fuse = connectedLine.fuse;
  const isAnliegend = fuse && !fuse.isInserted;

  // Resolve segment data
  const connectedSegs = findSegmentsConnectedToBus(busIndex, cable);
  const loading = connectedSegs.reduce((max, seg) => {
    const d = segments[seg.id];
    return d?.loadingPercent && d.loadingPercent > max ? d.loadingPercent : max;
  }, 0);
  const firstSeg = connectedSegs[0];
  const segData = firstSeg ? segments[firstSeg.id] : null;
  const loadStyles = getLoadStyles(loading, thresholds);

  // Fuse loading calculation — use max per-phase current (NH fuses are per-phase)
  const fuseWarning = cableLevels.orange;
  const fuseCritical = cableLevels.red;
  const currentA = maxPhaseCurrentA(segData);
  const fuseLoadingPct = fuse && fuse.isInserted && fuse.current > 0
    ? (currentA / fuse.current) * 100
    : null;
  const fuseStyles = fuseLoadingPct != null ? getFuseLoadStyles(fuseLoadingPct, fuseWarning, fuseCritical) : null;

  // Compact row shows whichever bottleneck is worse: cable or fuse
  const compactPct = fuseLoadingPct != null && fuseLoadingPct > loading ? fuseLoadingPct : loading;
  const compactStyles = fuseLoadingPct != null && fuseLoadingPct > loading
    ? getFuseLoadStyles(fuseLoadingPct, fuseWarning, fuseCritical)
    : loadStyles;

  const handleFuseToggle = () => {
    if (!fuse) return;

    if (fuse.isInserted) {
      // Removing fuse from holder — confirm first
      setShowFuseConfirm(true);
    } else {
      // Inserting fuse back — safe, no confirmation needed
      kvsService.toggleFuseInserted(kvsId, cable.id, true).then(() => onUpdate());
    }
  };

  const confirmFuseRemove = () => {
    setShowFuseConfirm(false);
    kvsService.toggleFuseInserted(kvsId, cable.id, false).then(() => onUpdate());
  };

  const handleFuseTypeChange = (newType: string) => {
    if (!fuse) return;
    const ratings = getRatingsForFuseType(newType);
    // Keep current rating if valid for new type, otherwise pick first available
    const newCurrent = ratings.includes(fuse.current) ? fuse.current : (ratings[0] ?? fuse.current);
    kvsService.updateFuse(kvsId, cable.id, { type: newType, current: newCurrent, isInserted: fuse.isInserted }).then(() => onUpdate());
  };

  const handleFuseCurrentChange = (newCurrent: number) => {
    if (!fuse) return;
    kvsService.updateFuse(kvsId, cable.id, { type: fuse.type, current: newCurrent, isInserted: fuse.isInserted }).then(() => onUpdate());
  };

  // Speaking cable names need the endpoint elements (same labels as map/cable view)
  const getElement = useNetworkElementsStore(state => state.getElement);
  const resolveElement = React.useCallback((id: string) => getElement(id), [getElement]);

  return (
    <div className={isAnliegend ? 'opacity-60' : ''}>
      {/* Compact row — two lines so cable name stays readable */}
      <div
        className={`flex items-start gap-1.5 px-1.5 py-1 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-primary-500/50 text-xs ${
          isExpanded ? 'bg-gray-100 dark:bg-primary-500/50' : ''
        }`}
      >
        {/* Drag handle */}
        <div
          draggable
          onDragStart={(e) => onDragStart(e, cable.id)}
          className="shrink-0 cursor-grab active:cursor-grabbing p-0.5 mt-0.5"
          title="Ziehen um zuzuordnen"
        >
          <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" />
          </svg>
        </div>

        {/* Clickable area for expand/collapse */}
        <div
          className="flex flex-col gap-0.5 flex-1 min-w-0"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {/* Line 1: Cable name (full width) + chevron */}
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className={`truncate font-medium flex-1 min-w-0 ${isAnliegend ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'}`}
              title={cable.name || cable.id}
            >
              {cableDisplayName(cable, resolveElement)}
            </span>
            <svg
              className={`w-3 h-3 text-gray-400 shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Line 2: Metadata badges + loading */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Cross section */}
            <span className="shrink-0 text-[10px] text-gray-400 dark:text-gray-500">
              {cable.crossSection}mm²
            </span>

            {/* Fuse badge — toggles inserted state on click */}
            {fuse ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFuseToggle();
                }}
                className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded cursor-pointer transition-colors ${
                  fuse.isInserted && fuseStyles
                    ? `${fuseStyles.badge} hover:opacity-80`
                    : fuse.isInserted
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                title={fuse.isInserted ? 'Sicherung herausnehmen' : 'Sicherung einlegen'}
              >
                {fuse.type} {fuse.current}A{!fuse.isInserted ? ' (n.e.)' : fuseLoadingPct != null ? ` (${fmt(fuseLoadingPct, 0)}%)` : ''}
              </button>
            ) : (
              <span className="shrink-0 text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded">
                Keine Sich.
              </span>
            )}

            {/* Mini loading bar + percent (shows max of cable/fuse loading) */}
            <div className="flex items-center gap-1 ml-auto shrink-0">
              <div className="w-12 h-1.5 bg-gray-200 dark:bg-primary-600 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${'mini' in compactStyles ? compactStyles.mini : compactStyles.bar}`} style={{ width: `${Math.min(compactPct, 100)}%` }} />
              </div>
              <span className={`text-[10px] font-medium w-9 text-right ${compactStyles.text}`}>
                {fmt(compactPct, 1)}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded detail panel */}
      {isExpanded && (
        <div className="ml-6 mr-1 mb-1.5 mt-0.5 rounded-md border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-800/50 p-3">
          {/* Cable name + type */}
          <div className="mb-3">
            <div className="text-xs font-medium text-gray-700 dark:text-gray-200 wrap-break-word" title={cable.name || cable.id}>
              {cableDisplayName(cable, resolveElement)}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
              {cableTypeLabel(cable)} · {cable.crossSection} mm²
            </div>
          </div>

          {/* Fuse section — read-only deployments show the fuse as text */}
          {fuse && readOnly && (
            <div className="mb-3">
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1.5">Sicherung</div>
              <div className="flex items-center gap-2 text-xs text-gray-800 dark:text-gray-200">
                <span className="font-medium">{fuse.type} {fuse.current} A</span>
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${
                  fuse.isInserted
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                }`}>
                  {fuse.isInserted ? 'Eingelegt' : 'Nicht eingelegt'}
                </span>
              </div>
            </div>
          )}
          {fuse && !readOnly && (
            <div className="mb-3">
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1.5">Sicherung</div>
              <div className="flex items-center gap-2">
                {/* Fuse type selector */}
                <select
                  value={fuse.type}
                  onChange={(e) => handleFuseTypeChange(e.target.value)}
                  className="text-xs border border-gray-300 dark:border-primary-500 bg-white dark:bg-primary-600 text-gray-900 dark:text-white rounded-md px-2 py-1"
                >
                  {fuseTypes.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>

                {/* Fuse current selector */}
                <select
                  value={fuse.current}
                  onChange={(e) => handleFuseCurrentChange(parseInt(e.target.value))}
                  className="text-xs border border-gray-300 dark:border-primary-500 bg-white dark:bg-primary-600 text-gray-900 dark:text-white rounded-md px-2 py-1 w-20"
                >
                  {getRatingsForFuseType(fuse.type).map(r => (
                    <option key={r} value={r}>{r}A</option>
                  ))}
                </select>

                {/* Inserted/removed toggle */}
                <button
                  onClick={handleFuseToggle}
                  className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                    fuse.isInserted
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                      : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50'
                  }`}
                >
                  {fuse.isInserted ? 'Eingelegt' : 'Nicht eingelegt'}
                </button>
              </div>
            </div>
          )}

          {/* Cable loading bar */}
          <div className={`${loadStyles.bg} rounded-md px-3 py-2 mb-3`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-gray-500 dark:text-gray-400">Kabelauslastung</span>
              <span className={`text-sm font-bold ${loadStyles.text}`}>{fmt(loading, 1)}%</span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-primary-600 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${loadStyles.bar} transition-all`} style={{ width: `${Math.min(loading, 100)}%` }} />
            </div>
          </div>

          {/* Fuse loading bar */}
          {fuseLoadingPct != null && fuseStyles && (
            <div className={`${fuseStyles.bg} rounded-md px-3 py-2 mb-3`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] text-gray-500 dark:text-gray-400">Sicherungsauslastung</span>
                <span className={`text-sm font-bold ${fuseStyles.text}`}>{fmt(fuseLoadingPct, 1)}%</span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 dark:bg-primary-600 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${fuseStyles.bar} transition-all`} style={{ width: `${Math.min(fuseLoadingPct, 100)}%` }} />
              </div>
              <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                {fmt(currentA, 1)} A / {fuse!.current} A
              </div>
            </div>
          )}

          {/* Power + Current */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
              <div className="text-sm font-bold text-gray-900 dark:text-white">
                {segData?.powerKW ? fmt(Math.abs(segData.powerKW), 1) : '0'} <span className="text-[10px] font-normal text-gray-500 dark:text-gray-400">kW</span>
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">Leistung</div>
            </div>
            <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
              <div className="text-sm font-bold text-gray-900 dark:text-white">
                {segData?.currentA ? fmt(Math.abs(segData.currentA), 1) : '0'} <span className="text-[10px] font-normal text-gray-500 dark:text-gray-400">A</span>
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400">Strom</div>
            </div>
          </div>

          {/* Remove button */}
          {!readOnly && (
            <div className="flex justify-end">
              <RemoveCableButton
                kvsId={kvsId}
                cableId={cable.id}
                onRemoved={onUpdate}
              />
            </div>
          )}
        </div>
      )}

      {/* Confirm dialog for removing fuse from holder */}
      {showFuseConfirm && fuse && (
        <ConfirmDialog
          tone="danger"
          title="Sicherung herausnehmen"
          message={`Soll die Sicherung ${fuse.type} ${fuse.current}A von "${cable.name || cable.id}" wirklich herausgenommen werden? Das Kabel wird dadurch stromlos (anliegend).`}
          confirmLabel="Herausnehmen"
          cancelLabel="Abbrechen"
          onConfirm={confirmFuseRemove}
          onCancel={() => setShowFuseConfirm(false)}
        />
      )}
    </div>
  );
};
